import React from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { FaTimes, FaPlus } from 'react-icons/fa';
import SelectTextField from '../../../../molecules/SelectTextField';
import CurrencyInput from '../../../../molecules/CurrencyInput';
import TextEdit from '../../../../molecules/TextEdit';
import { SecurityItem } from '../../types/operations';
import { applyDateMask } from '../../../../../utils/dateMask';

interface SecurityItemFormProps {
    items: SecurityItem[];
    onAdd: (item: SecurityItem) => void;
    onRemove: (index: number) => void;
    disabled?: boolean;
}

const MIN_ROWS = 5;

const generateId = (): string =>
    crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const securityTypeOptions = [
    { value: 'CDBV', label: 'CDBV' },
    { value: 'LCA', label: 'LCA' },
    { value: 'LCI', label: 'LCI' },
    { value: 'CRI', label: 'CRI' },
    { value: 'CRA', label: 'CRA' },
];

const formatCurrency = (val: number): string =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const SecurityItemForm: React.FC<SecurityItemFormProps> = ({ items, onAdd, onRemove, disabled = false }) => {
    const securityType = useSignal('');
    const initialValue = useSignal(0);
    const interestRate = useSignal('');
    const dueDate = useSignal('');
    const issuer = useSignal('');
    const description = useSignal('');

    const resetForm = () => {
        securityType.value = '';
        initialValue.value = 0;
        interestRate.value = '';
        dueDate.value = '';
        issuer.value = '';
        description.value = '';
    };

    const handleAdd = () => {
        if (!securityType.value) { toast.error('Selecione o tipo de título'); return; }
        if (initialValue.value <= 0) { toast.error('Informe o valor inicial'); return; }
        if (!interestRate.value) { toast.error('Informe a taxa de juros'); return; }
        if (!dueDate.value) { toast.error('Informe o vencimento'); return; }

        onAdd({
            collateral_id: generateId(),
            security_type: securityType.value as SecurityItem['security_type'],
            initial_value: initialValue.value,
            interest_rate: Number(interestRate.value),
            due_date: dueDate.value.trim(),
            issuer: issuer.value.trim() || undefined,
            description: description.value.trim() || undefined,
        });
        resetForm();
    };

    const emptyRowCount = Math.max(0, MIN_ROWS - items.length);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-12 gap-3">
                <div className="col-span-2">
                    <SelectTextField label="Tipo Título *" items={securityTypeOptions} value={securityType.value} onChange={(e) => { securityType.value = e.target.value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <CurrencyInput label="Valor Inicial (R$) *" value={initialValue.value} onChange={(v) => { initialValue.value = v ?? 0; }} disabled={disabled} fractionDigits={2} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Taxa (% a.a.) *" type="number" value={interestRate.value} onChange={(e) => { interestRate.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Vencimento *" value={dueDate.value} onChange={(e) => { dueDate.value = applyDateMask((e.target as HTMLInputElement).value); }} placeholder="dd/mm/aaaa" disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Emissor" value={issuer.value} onChange={(e) => { issuer.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2 flex items-end">
                    <button type="button" onClick={handleAdd} disabled={disabled} className="flex items-center gap-1 px-3 py-2 mb-4 bg-gray-600 dark:bg-gray-500 text-white text-sm rounded hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">
                        <FaPlus size={12} /> Adicionar
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-600 rounded">
                <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
                    <thead className="text-xs uppercase bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                        <tr>
                            <th className="px-3 py-2">Tipo</th>
                            <th className="px-3 py-2">Valor</th>
                            <th className="px-3 py-2">Taxa (% a.a.)</th>
                            <th className="px-3 py-2">Vencimento</th>
                            <th className="px-3 py-2">Emissor</th>
                            <th className="px-3 py-2 w-16">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={item.collateral_id} className={`${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} border-b border-gray-200 dark:border-gray-600`}>
                                <td className="px-3 py-2">{item.security_type}</td>
                                <td className="px-3 py-2">{formatCurrency(item.initial_value)}</td>
                                <td className="px-3 py-2">{item.interest_rate}%</td>
                                <td className="px-3 py-2">{item.due_date}</td>
                                <td className="px-3 py-2">{item.issuer || '-'}</td>
                                <td className="px-3 py-2">
                                    <button type="button" onClick={() => onRemove(index)} disabled={disabled} className="text-red-600 dark:text-red-400 hover:text-red-800 disabled:opacity-50" title="Remover">
                                        <FaTimes size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {Array.from({ length: emptyRowCount }).map((_, i) => (
                            <tr key={`empty-${i}`} className={`${(items.length + i) % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} border-b border-gray-200 dark:border-gray-600`}>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SecurityItemForm;
